import { useState, useEffect, useRef } from "react";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { UserRoundMinus } from "lucide-react";
// Components
import Button from "../components/Button";
import PersonalDetails from "../components/sectionWrapper/PersonalDetails";
import TravelDetails from "../components/sectionWrapper/TravelDetails";
import HealthDetails from "../components/sectionWrapper/HealthDetails";
import { validateField } from "../components/userForms/validators";
import { validationRules } from "../components/userForms/validationRules";
import ConfirmPopup from "components/popups/ConfirmPopup";
import AirTravelDetails from "components/sectionWrapper/AirTravelDetails";

export default function Participants({
  index,
  participantData,
  tabId,
  handleAddParticipantTab,
  handleRemoveParticipantTab,
  activeView,
  formErrors,
  onParticipantsChange,
  participantError,
  setParticipantErrors,
  setFormErrors,
  participantOutboundFlights,
  participantInboundFlights,
  setParticipantOutboundFlights,
  setParticipantInboundFlights,
  setAllParticipantsData,
  autoFillOptions,
  setInboundFlights,
  setOutboundFlights,
  fieldRefs,
  setEditedField,
  setEditedScope,
}) {
  const topRef = useRef(null);
  const [participant, setParticipant] = useState({});
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    if (activeView === tabId) {
      topRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [activeView, tabId]);

  useEffect(() => {
    setParticipant(participantData);
  }, [participantData]);

  const handleParticipantError = (name, value) => {
    const newValue = { [name]: value };
    setParticipantErrors((prevData) => {
      const newData = [...prevData];
      if (newData[index])
        newData[index] = {
          ...newData[index], // keep existing participant fields
          ...newValue, // override with new values
        };
      else {
        newData[index] = {
          ...newValue, // override with new values
        };
      }
      return newData;
    });
  };

  const handleParticipantChange = (index, e) => {
    if (!e || !e.target) {
      console.error("handleParticipantChange called without valid event:", e);
      return;
    }

    const { name, value } = e.target;

    if (!name) {
      console.error("Missing name attribute on input element:", e.target);
      return; // prevent crash
    }

    const fieldName = name.replace(/\d+$/, "");

    if (e.isTrusted) {
      setEditedField(name);
      setEditedScope("participant");
    }

    setParticipant((prev) => {
      const updated = { ...prev };
      updated.formData = {
        ...updated.formData,
        [fieldName]: value,
      };
      onParticipantsChange?.(index, updated, true, e);
      return updated;
    });

    const error = validateField(
      fieldName,
      value,
      validationRules(participant.formData)
    );

    setParticipantErrors((prev) => {
      const newData = [...prev]; // clone the array
      const participantError = newData[index] ? { ...newData[index] } : {};
      participantError[fieldName] = error;
      newData[index] = participantError; // update the error at the correct index
      return newData; // return the updated array
    });
  };

  return (
    <>
      {participant && (
        <div ref={topRef} className="scroll-m-20">
          <div
            key={participant.id}
            className="mx-4 sm:mx-10 my-4 border border-gray-300 mb-6 rounded-md"
          >
            <div className="flex justify-between gap-2 items-center px-2 py-2 bg-gray-100 rounded-t-md">
              {/* <h3 className="font-semibold">
                {"Participant " + (index + 1)}
                Second Traveller
              </h3> */}
              <div className="">
                <h3 className="font-semibold">Second Traveller</h3>
                <div className="flex items-start gap-1">
                  <AiOutlineInfoCircle className="text-yellow-600 mt-[2px] md:size-3 size-6" />
                  <p className="text-xs text-yellow-800">
                    Already registered? Just enter your email — your personal
                    details will be filled in automatically.
                  </p>
                </div>
              </div>
              <Button
                onClick={() => {
                  setShowPopup(true);
                  setParticipantOutboundFlights([]);
                  setParticipantInboundFlights([]);
                }}
                text="Remove"
                iconEnd={<UserRoundMinus size={18} />}
                className="text-red-500 text-sm font-semibold px-[10px] py-[6px] border border-red-500"
                // hideTextOnMobile={true}
              />
            </div>

            {/* Participant Personal Details */}
            <section
              id="section1"
              className="px-4 sm:px-6 py-4 border-b border-sectionBorder"
            >
              <h4 className="mb-2">Personal Details</h4>
              <PersonalDetails
                formData={participant.formData ?? {}}
                handleChange={(e) => handleParticipantChange(index, e)}
                formErrors={participantError ?? {}}
                isParticipant={true}
                index={index}
                setAllParticipantsData={setAllParticipantsData}
                autoFillOptions={autoFillOptions}
                setParticipantErrors={handleParticipantError}
                inputRefs={fieldRefs} // 👈 pass refs here
              />
            </section>

            {/* Participant Travel Details */}
            <section id="section3" className="px-4 sm:px-6 py-4">
              <h4 className="mb-2">Travel Details</h4>
              <TravelDetails
                formData={participant.formData ?? {}}
                handleChange={(e) => handleParticipantChange(index, e)}
                formErrors={participantError ?? {}}
                index={index}
                inputRefs={fieldRefs} // 👈 pass refs here
              />
            </section>

            {/* Participant Health Details */}
            <section
              id="section4"
              className="px-4 sm:px-6 py-4 border-b border-sectionBorder"
            >
              <h2 className="mb-2">Health Details</h2>
              <HealthDetails
                formData={participant.formData ?? {}}
                handleChange={(e) => handleParticipantChange(index, e)}
                formErrors={participantError ?? {}}
                index={index}
                inputRefs={fieldRefs} // 👈 pass refs here
              />
            </section>

            {/* Participant Air Travel Details */}
            <section
              id="section5"
              className="px-4 sm:px-10 py-4 border-b border-sectionBorder"
            >
              <div className="mb-4">
                <h2>Air Travel Details</h2>
                <div className="flex items-center gap-1 text-xs text-yellow-800">
                  <AiOutlineInfoCircle size={14} className="text-yellow-600" />
                  <p className="text-sm">
                    Please complete this section after purchasing your flight
                    ticket.
                  </p>
                </div>
              </div>

              <AirTravelDetails
                isParticipant={true}
                outboundFlights={participantOutboundFlights}
                setOutboundFlights={setParticipantOutboundFlights}
                inboundFlights={participantInboundFlights}
                setInboundFlights={setParticipantInboundFlights}
                formErrors={participantError ?? {}}
                handleChange={(e) => handleParticipantChange(index, e)}
                setFormErrors={setFormErrors}
                index={index}
                copyOutBoundFlights={setOutboundFlights}
                copyInBoundFlights={setInboundFlights}
                inputRefs={fieldRefs} // 👈 pass refs here
              />
            </section>
          </div>

          {showPopup && (
            <ConfirmPopup
              onCancel={() => setShowPopup(false)}
              onConfirm={() => handleRemoveParticipantTab(tabId, index)}
            />
          )}
        </div>
      )}
    </>
  );
}
